import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, FolderOpen, Gift, CheckCircle2, BarChart3, Building2, Plus, Trash2, Calendar, DollarSign, Pencil, X, UserPlus } from "lucide-react";

const TABS = [
  ["overview", "Overview"],
  ["users", "Users"],
  ["tasks", "Tasks"],
  ["perks", "Perks"],
  ["challenges", "Challenges"],
  ["events", "Events"],
  ["orgs", "Organisations"],
  ["sponsors", "Sponsors"],
  ["sponsorships", "Sponsorships"],
  ["impact", "Impact"],
];

export default function Admin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams(t === "overview" ? {} : { tab: t });
  return (
    <div data-testid="admin-page">
      <div className="flex items-center gap-3">
        <Building2 className="text-neoncyan" size={28}/>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Admin</h1>
      </div>
      <p className="text-white/60 mt-1.5">Content, users, sponsorships & impact.</p>

      <div className="flex gap-2 mt-6 overflow-x-auto scrollbar-thin pb-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${tab === k ? "bg-neonpink text-white shadow-glow" : "bg-white/5 text-white/70 border border-white/10"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && <Overview/>}
        {tab === "users" && <UsersTab/>}
        {tab === "tasks" && <TasksTab/>}
        {tab === "perks" && <PerksTab/>}
        {tab === "challenges" && <ChallengesTab/>}
        {tab === "events" && <EventsTab/>}
        {tab === "orgs" && <OrgsTab/>}
        {tab === "sponsors" && <SponsorsTab/>}
        {tab === "sponsorships" && <SponsorshipsTab/>}
        {tab === "impact" && <ImpactTab/>}
      </div>
    </div>
  );
}

// ---------- Overview ----------
function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="text-white/60">Loading...</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      <Stat icon={Users} label="Users" value={s.total_users} color="#FF2A6D"/>
      <Stat icon={CheckCircle2} label="Completions" value={s.total_completions} color="#01FFC3"/>
      <Stat icon={FolderOpen} label="Categories" value={s.categories} color="#05D9E8"/>
      <Stat icon={BarChart3} label="Tasks" value={s.tasks} color="#FFD700"/>
      <Stat icon={Gift} label="Perks" value={s.perks} color="#FF2A6D"/>
      <Stat icon={Calendar} label="Events" value={s.events || 0} color="#FFD700"/>
      <Stat icon={DollarSign} label="Sponsorships" value={s.sponsorships_paid || 0} color="#01FFC3"/>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card p-5">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, color }}><Icon size={18}/></div>
      <div className="mt-3 font-display text-3xl font-black">{value}</div>
      <div className="text-xs text-white/55 uppercase tracking-widest">{label}</div>
    </div>
  );
}

// ---------- Users ----------
function UsersTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="glass-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-widest text-white/50">
          <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-right p-3">Lvl</th><th className="text-right p-3">XP</th><th className="text-right p-3">Done</th></tr>
        </thead>
        <tbody>
          {rows.map(u => (
            <tr key={u.user_id} className="border-t border-white/5" data-testid={`admin-user-row-${u.user_id}`}>
              <td className="p-3 font-semibold">{u.name}</td>
              <td className="p-3 text-white/70">{u.email}</td>
              <td className="p-3 capitalize">{u.role}</td>
              <td className="p-3 text-right">{u.level}</td>
              <td className="p-3 text-right text-neonyellow font-bold">{u.xp}</td>
              <td className="p-3 text-right">{u.completed_tasks?.length || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tasks ----------
function TasksTab() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/categories"), api.get("/categories/money-banking")]);
    setCats(c.data);
    // Aggregate all tasks across categories
    const all = [];
    for (const cat of c.data) {
      const { data } = await api.get(`/categories/${cat.category_id}`);
      all.push(...data.tasks);
    }
    setRows(all);
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      await api.post("/admin/tasks", form);
      toast.success("Task saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (task_id) => {
    if (!window.confirm(`Delete ${task_id}?`)) return;
    try {
      await api.delete(`/admin/tasks/${task_id}`);
      toast("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-white/60">{rows.length} tasks</span>
        <button onClick={() => setEditing({ task_id: "", category_id: cats[0]?.category_id, title: "", why: "", steps: [""], links: [], estimated_time: "30 mins", difficulty: "easy", points: 50, order: 99 })}
          data-testid="admin-add-task" className="btn-primary-neon text-sm flex items-center gap-2"><Plus size={14}/> Add task</button>
      </div>

      {editing && <TaskForm initial={editing} cats={cats} onCancel={() => setEditing(null)} onSave={save}/>}

      <div className="glass-card mt-4 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-white/50">
            <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Category</th><th className="text-right p-3">Pts</th><th className="text-right p-3 w-32">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.task_id} className="border-t border-white/5" data-testid={`admin-task-row-${r.task_id}`}>
                <td className="p-3 font-semibold">{r.title}</td>
                <td className="p-3 text-white/70 text-xs">{r.category_id}</td>
                <td className="p-3 text-right text-neonyellow font-bold">{r.points}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing(r)} data-testid={`admin-task-edit-${r.task_id}`} className="p-1.5 rounded-full bg-white/5 hover:bg-white/15"><Pencil size={12}/></button>
                  <button onClick={() => remove(r.task_id)} data-testid={`admin-task-delete-${r.task_id}`} className="p-1.5 rounded-full bg-red-500/15 hover:bg-red-500/30 text-red-300"><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TaskForm({ initial, cats, onCancel, onSave }) {
  const [f, setF] = useState({
    ...initial,
    steps: Array.isArray(initial.steps) ? initial.steps : [""],
    links: Array.isArray(initial.links) ? initial.links : [],
  });
  return (
    <div className="glass-card p-5 mb-3">
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-display font-bold text-lg">{initial.task_id ? "Edit" : "New"} task</h3>
        <button onClick={onCancel} className="p-2 rounded-full hover:bg-white/10"><X size={16}/></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Task ID (slug)" value={f.task_id} onChange={v => setF({ ...f, task_id: v })} testid="task-form-id" disabled={!!initial.task_id && initial.task_id !== ""}/>
        <Select label="Category" value={f.category_id} onChange={v => setF({ ...f, category_id: v })} options={cats.map(c => ({ v: c.category_id, l: c.name }))} testid="task-form-cat"/>
        <Input label="Title" value={f.title} onChange={v => setF({ ...f, title: v })} testid="task-form-title" full/>
        <Textarea label="Why it matters" value={f.why} onChange={v => setF({ ...f, why: v })} testid="task-form-why" full/>
        <Input label="Estimated time" value={f.estimated_time} onChange={v => setF({ ...f, estimated_time: v })} testid="task-form-time"/>
        <Select label="Difficulty" value={f.difficulty} onChange={v => setF({ ...f, difficulty: v })} options={[{v:"easy",l:"Easy"},{v:"medium",l:"Medium"},{v:"hard",l:"Hard"}]} testid="task-form-diff"/>
        <Input label="Points" type="number" value={f.points} onChange={v => setF({ ...f, points: parseInt(v || 0) })} testid="task-form-points"/>
        <Input label="Order" type="number" value={f.order} onChange={v => setF({ ...f, order: parseInt(v || 0) })} testid="task-form-order"/>
        <Textarea label="Steps (one per line)" value={(f.steps || []).join("\n")} onChange={v => setF({ ...f, steps: v.split("\n").filter(Boolean) })} testid="task-form-steps" full rows={5}/>
        <Textarea label="Links (LABEL|URL per line)"
          value={(f.links || []).map(l => `${l.label}|${l.url}`).join("\n")}
          onChange={v => setF({ ...f, links: v.split("\n").filter(Boolean).map(line => { const [lbl, url] = line.split("|"); return { label: lbl?.trim() || "", url: (url || "").trim() }; }) })}
          testid="task-form-links" full rows={3}/>
      </div>
      <button onClick={() => onSave(f)} data-testid="task-form-save" className="btn-primary-neon text-sm mt-4">Save task</button>
    </div>
  );
}

// ---------- Perks ----------
function PerksTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/perks").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try { await api.post("/admin/perks", form); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (perk_id) => {
    if (!window.confirm(`Delete ${perk_id}?`)) return;
    try { await api.delete(`/admin/perks/${perk_id}`); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-white/60">{rows.length} perks</span>
        <button onClick={() => setEditing({ perk_id: "", title: "", partner: "", category: "Shopping", description: "", url: "", image: "", points_required: 0, tag: "Free" })}
          data-testid="admin-add-perk" className="btn-primary-neon text-sm flex items-center gap-2"><Plus size={14}/> Add perk</button>
      </div>
      {editing && (
        <div className="glass-card p-5 mb-3">
          <div className="flex justify-between items-start mb-4">
            <h3 className="font-display font-bold text-lg">{editing.perk_id ? "Edit" : "New"} perk</h3>
            <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-white/10"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Perk ID" value={editing.perk_id} onChange={v => setEditing({ ...editing, perk_id: v })} testid="perk-form-id"/>
            <Input label="Title" value={editing.title} onChange={v => setEditing({ ...editing, title: v })} testid="perk-form-title"/>
            <Input label="Partner" value={editing.partner} onChange={v => setEditing({ ...editing, partner: v })} testid="perk-form-partner"/>
            <Input label="Category" value={editing.category} onChange={v => setEditing({ ...editing, category: v })} testid="perk-form-category"/>
            <Input label="URL" value={editing.url} onChange={v => setEditing({ ...editing, url: v })} testid="perk-form-url" full/>
            <Input label="Image URL" value={editing.image} onChange={v => setEditing({ ...editing, image: v })} testid="perk-form-image" full/>
            <Textarea label="Description" value={editing.description} onChange={v => setEditing({ ...editing, description: v })} testid="perk-form-desc" full/>
            <Input label="Points required" type="number" value={editing.points_required} onChange={v => setEditing({ ...editing, points_required: parseInt(v || 0) })} testid="perk-form-points"/>
            <Select label="Tag" value={editing.tag} onChange={v => setEditing({ ...editing, tag: v })} options={["Free","Student","Discount","Premium","Trial"].map(x => ({v:x,l:x}))} testid="perk-form-tag"/>
          </div>
          <button onClick={() => save(editing)} data-testid="perk-form-save" className="btn-primary-neon text-sm mt-4">Save</button>
        </div>
      )}
      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-white/50">
            <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Partner</th><th className="text-left p-3">Tag</th><th className="text-right p-3">XP</th><th className="text-right p-3 w-32">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.perk_id} className="border-t border-white/5">
                <td className="p-3 font-semibold">{r.title}</td>
                <td className="p-3 text-white/70">{r.partner}</td>
                <td className="p-3 text-xs">{r.tag}</td>
                <td className="p-3 text-right">{r.points_required}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing(r)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/15"><Pencil size={12}/></button>
                  <button onClick={() => remove(r.perk_id)} className="p-1.5 rounded-full bg-red-500/15 text-red-300"><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Challenges ----------
function ChallengesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/challenges").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try { await api.post("/admin/challenges", form); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (challenge_id) => {
    if (!window.confirm(`Delete ${challenge_id}?`)) return;
    try { await api.delete(`/admin/challenges/${challenge_id}`); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-white/60">{rows.length} challenges</span>
        <button onClick={() => setEditing({ challenge_id: "", title: "", description: "", category_id: null, duration_days: 7, reward_points: 100, active: true, sponsor_name: "", sponsor_logo: "", sponsor_url: "" })}
          data-testid="admin-add-challenge" className="btn-primary-neon text-sm flex items-center gap-2"><Plus size={14}/> Add</button>
      </div>
      {editing && (
        <div className="glass-card p-5 mb-3">
          <div className="flex justify-between items-start mb-4"><h3 className="font-display font-bold text-lg">{editing.challenge_id ? "Edit" : "New"} challenge</h3><button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-white/10"><X size={16}/></button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="ID" value={editing.challenge_id} onChange={v => setEditing({ ...editing, challenge_id: v })}/>
            <Input label="Title" value={editing.title} onChange={v => setEditing({ ...editing, title: v })}/>
            <Textarea label="Description" value={editing.description} onChange={v => setEditing({ ...editing, description: v })} full/>
            <Input label="Category ID (optional)" value={editing.category_id || ""} onChange={v => setEditing({ ...editing, category_id: v || null })}/>
            <Input label="Duration (days)" type="number" value={editing.duration_days} onChange={v => setEditing({ ...editing, duration_days: parseInt(v || 0) })}/>
            <Input label="Reward XP" type="number" value={editing.reward_points} onChange={v => setEditing({ ...editing, reward_points: parseInt(v || 0) })}/>
            <Input label="Sponsor name" value={editing.sponsor_name || ""} onChange={v => setEditing({ ...editing, sponsor_name: v || null })}/>
            <Input label="Sponsor logo URL" value={editing.sponsor_logo || ""} onChange={v => setEditing({ ...editing, sponsor_logo: v || null })}/>
          </div>
          <button onClick={() => save(editing)} className="btn-primary-neon text-sm mt-4">Save</button>
        </div>
      )}
      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-white/50"><tr><th className="text-left p-3">Title</th><th className="text-left p-3">Sponsor</th><th className="text-right p-3">XP</th><th className="text-right p-3 w-32">Actions</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.challenge_id} className="border-t border-white/5">
                <td className="p-3 font-semibold">{r.title}</td>
                <td className="p-3 text-neonyellow text-xs">{r.sponsor_name || "—"}</td>
                <td className="p-3 text-right">{r.reward_points}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing(r)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/15"><Pencil size={12}/></button>
                  <button onClick={() => remove(r.challenge_id)} className="p-1.5 rounded-full bg-red-500/15 text-red-300"><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Events ----------
function EventsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [ebUrl, setEbUrl] = useState("");
  const [ebBusy, setEbBusy] = useState(false);
  const load = () => api.get("/admin/events").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try { await api.post("/admin/events", form); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (event_id) => {
    if (!window.confirm(`Delete ${event_id}?`)) return;
    try { await api.delete(`/admin/events/${event_id}`); load(); } catch { toast.error("Delete failed"); }
  };

  const importEventbrite = async () => {
    if (!ebUrl.trim()) return;
    setEbBusy(true);
    try {
      const { data } = await api.post("/admin/events/import-eventbrite", { url_or_id: ebUrl.trim() });
      toast.success(`Imported: ${data.event.title}`);
      setEbUrl("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setEbBusy(false);
    }
  };

  return (
    <div>
      <div className="glass-card p-5 mb-4" data-testid="eventbrite-import-card">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-xl bg-neoncyan/15 text-neoncyan flex items-center justify-center"><Calendar size={16}/></div>
          <div>
            <h3 className="font-display font-bold">Import from Eventbrite</h3>
            <p className="text-xs text-white/55">Paste any Eventbrite event URL or numeric ID — we'll auto-fill title, venue, date and link.</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <input value={ebUrl} onChange={e => setEbUrl(e.target.value)}
            placeholder="https://www.eventbrite.com.au/e/some-event-title-tickets-1234567890"
            data-testid="eventbrite-url-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-neoncyan"/>
          <button onClick={importEventbrite} disabled={ebBusy || !ebUrl.trim()}
            data-testid="eventbrite-import-button"
            className="btn-primary-neon text-sm whitespace-nowrap disabled:opacity-50">
            {ebBusy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-white/60">{rows.length} events</span>
        <button onClick={() => setEditing({ event_id: "", title: "", description: "", organiser: "", category: "Workshop", state: "NSW", city: "Sydney", venue: "", online: false, starts_at: new Date().toISOString(), ends_at: new Date().toISOString(), cover_image: "", url: "", rsvp_required: true, free: true })}
          data-testid="admin-add-event" className="btn-primary-neon text-sm flex items-center gap-2"><Plus size={14}/> Add event</button>
      </div>
      {editing && (
        <div className="glass-card p-5 mb-3">
          <div className="flex justify-between items-start mb-4"><h3 className="font-display font-bold text-lg">{editing.event_id ? "Edit" : "New"} event</h3><button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-white/10"><X size={16}/></button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Event ID" value={editing.event_id} onChange={v => setEditing({ ...editing, event_id: v })}/>
            <Input label="Title" value={editing.title} onChange={v => setEditing({ ...editing, title: v })}/>
            <Textarea label="Description" value={editing.description} onChange={v => setEditing({ ...editing, description: v })} full/>
            <Input label="Organiser" value={editing.organiser} onChange={v => setEditing({ ...editing, organiser: v })}/>
            <Select label="Category" value={editing.category} onChange={v => setEditing({ ...editing, category: v })} options={["University","Workshop","Career","Wellbeing","Social"].map(x => ({v:x,l:x}))}/>
            <Select label="State" value={editing.state} onChange={v => setEditing({ ...editing, state: v })} options={["ALL","NSW","VIC","QLD","WA","SA","TAS","ACT","NT"].map(x => ({v:x,l:x}))}/>
            <Input label="City" value={editing.city} onChange={v => setEditing({ ...editing, city: v })}/>
            <Input label="Venue" value={editing.venue} onChange={v => setEditing({ ...editing, venue: v })}/>
            <Input label="Starts (ISO)" value={editing.starts_at} onChange={v => setEditing({ ...editing, starts_at: v })}/>
            <Input label="Ends (ISO)" value={editing.ends_at} onChange={v => setEditing({ ...editing, ends_at: v })}/>
            <Input label="Cover image URL" value={editing.cover_image || ""} onChange={v => setEditing({ ...editing, cover_image: v })} full/>
            <Input label="External URL" value={editing.url || ""} onChange={v => setEditing({ ...editing, url: v })} full/>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.online} onChange={e => setEditing({ ...editing, online: e.target.checked })}/> Online</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.free} onChange={e => setEditing({ ...editing, free: e.target.checked })}/> Free</label>
          </div>
          <button onClick={() => save(editing)} className="btn-primary-neon text-sm mt-4">Save</button>
        </div>
      )}
      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-white/50"><tr><th className="text-left p-3">Title</th><th className="text-left p-3">Organiser</th><th className="text-left p-3">State</th><th className="text-left p-3">When</th><th className="text-right p-3 w-32">Actions</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.event_id} className="border-t border-white/5">
                <td className="p-3 font-semibold">{r.title}</td>
                <td className="p-3 text-white/70 text-xs">{r.organiser}</td>
                <td className="p-3 text-xs">{r.state}</td>
                <td className="p-3 text-xs text-white/55">{new Date(r.starts_at).toLocaleDateString("en-AU")}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing(r)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/15"><Pencil size={12}/></button>
                  <button onClick={() => remove(r.event_id)} className="p-1.5 rounded-full bg-red-500/15 text-red-300"><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Orgs ----------
function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("university");
  const [email, setEmail] = useState("");
  const load = () => api.get("/admin/organisations").then(({ data }) => setOrgs(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name || !email) return;
    try {
      const { data } = await api.post("/admin/organisations", { name, type, contact_email: email });
      toast.success(`Code: ${data.invite_code}`);
      setName(""); setEmail(""); load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="glass-card p-6 lg:col-span-2">
        <h3 className="font-display font-bold text-lg mb-3">Organisations</h3>
        <div className="space-y-2">
          {orgs.map(o => (
            <div key={o.organisation_id} className="flex items-center justify-between p-3 rounded-xl bg-white/5">
              <div><div className="font-bold">{o.name}</div><div className="text-xs text-white/55 capitalize">{o.type} · {o.contact_email}</div></div>
              <code className="text-xs text-neoncyan bg-white/10 px-2 py-1 rounded">{o.invite_code}</code>
            </div>
          ))}
          {orgs.length === 0 && <div className="text-white/55 text-sm">None yet.</div>}
        </div>
      </div>
      <div className="glass-card p-6">
        <h3 className="font-display font-bold text-lg">Create</h3>
        <div className="space-y-3 mt-3">
          <Input label="Name" value={name} onChange={setName}/>
          <Select label="Type" value={type} onChange={setType} options={[{v:"university",l:"University"},{v:"school",l:"School"},{v:"council",l:"Council"},{v:"gov",l:"Government"},{v:"youth_org",l:"Youth Org"}]}/>
          <Input label="Contact email" value={email} onChange={setEmail}/>
          <button onClick={create} className="btn-primary-neon text-sm w-full">Create</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Sponsorships ----------
function SponsorshipsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/sponsorships").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="text-white/60">Loading...</div>;
  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <Stat icon={DollarSign} label="Paid sponsorships ($AUD)" value={`$${data.total_paid.toFixed(0)}`} color="#01FFC3"/>
        <Stat icon={BarChart3} label="Total transactions" value={data.transactions.length} color="#FFD700"/>
      </div>
      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-white/50"><tr><th className="text-left p-3">Sponsor</th><th className="text-left p-3">Email</th><th className="text-left p-3">Package</th><th className="text-right p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Date</th></tr></thead>
          <tbody>
            {data.transactions.map(t => (
              <tr key={t.session_id} className="border-t border-white/5">
                <td className="p-3 font-semibold">{t.sponsor_name}</td>
                <td className="p-3 text-white/70 text-xs">{t.sponsor_email}</td>
                <td className="p-3 text-xs capitalize">{t.package_id}</td>
                <td className="p-3 text-right text-neonyellow font-bold">${t.amount}</td>
                <td className="p-3 text-xs"><span className={`px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-neongreen/15 text-neongreen" : "bg-white/10 text-white/60"}`}>{t.payment_status}</span></td>
                <td className="p-3 text-xs text-white/55">{new Date(t.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {data.transactions.length === 0 && <tr><td colSpan={6} className="text-center p-8 text-white/55">No sponsorships yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Impact ----------
function ImpactTab() {
  const [impact, setImpact] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { api.get("/admin/impact").then(({ data }) => setImpact(data)); }, []);

  const exportCsv = () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${apiBase}/api/admin/impact/export.csv`, "_blank");
  };

  const triggerPush = async (path, label) => {
    setPushBusy(true);
    try {
      const { data } = await api.post(path);
      toast.success(`${label}: delivered to ${data.delivered}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed");
    } finally { setPushBusy(false); }
  };

  if (!impact) return <div className="text-white/60">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="impact-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total users" value={impact.total_users} color="#FF2A6D"/>
        <Stat icon={Users} label="Active (7d)" value={impact.active_users_7d} color="#05D9E8"/>
        <Stat icon={CheckCircle2} label="Tasks completed" value={impact.total_completions} color="#01FFC3"/>
        <button onClick={exportCsv} data-testid="impact-export-csv"
          className="glass-card p-5 text-left hover:bg-white/[0.07] transition group">
          <div className="w-10 h-10 rounded-xl bg-neonyellow/15 text-neonyellow flex items-center justify-center mb-3 group-hover:scale-110 transition"><BarChart3 size={18}/></div>
          <div className="text-base font-display font-bold">Export CSV</div>
          <div className="text-xs text-white/55 mt-0.5">All users, anonymised on request</div>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="glass-card p-6">
          <h3 className="font-display font-bold text-lg">Completions by category</h3>
          <div className="space-y-2 mt-3">
            {impact.category_breakdown.map(c => {
              const max = Math.max(...impact.category_breakdown.map(x => x.completions), 1);
              return (
                <div key={c.category}>
                  <div className="flex justify-between text-xs text-white/70"><span>{c.category}</span><span>{c.completions}</span></div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-1"><div className="h-full bg-neoncyan" style={{ width: `${(c.completions / max) * 100}%` }}/></div>
                </div>
              );
            })}
            {impact.category_breakdown.length === 0 && <div className="text-white/55 text-sm">No completions yet.</div>}
          </div>
        </div>

        <BreakdownCard title="By age range" items={impact.age_breakdown} color="#FF2A6D"/>
        <BreakdownCard title="By study / work" items={impact.work_breakdown} color="#FFD700"/>
        <BreakdownCard title="By state" items={impact.state_breakdown} color="#01FFC3"/>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-display font-bold text-lg">Push notifications</h3>
        <p className="text-xs text-white/55 mt-1">Send manually — useful for one-off announcements or to trigger the daily jobs early.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button disabled={pushBusy} onClick={() => triggerPush("/admin/push/test", "Test broadcast")}
            data-testid="push-test-broadcast" className="btn-secondary-neon text-sm">Test broadcast</button>
          <button disabled={pushBusy} onClick={() => triggerPush("/admin/push/streak-warning", "Streak warnings")}
            data-testid="push-streak-warning" className="btn-secondary-neon text-sm">Send streak warnings</button>
          <button disabled={pushBusy} onClick={() => triggerPush("/admin/push/weekly-digest", "Weekly digest")}
            data-testid="push-weekly-digest" className="btn-secondary-neon text-sm">Send weekly digest</button>
        </div>
      </div>
    </div>
  );
}

function BreakdownCard({ title, items, color }) {
  const max = Math.max(...(items || []).map(i => i.count), 1);
  return (
    <div className="glass-card p-6">
      <h3 className="font-display font-bold text-lg">{title}</h3>
      <div className="space-y-2 mt-3">
        {(items || []).map(i => (
          <div key={i.label}>
            <div className="flex justify-between text-xs text-white/70"><span className="truncate pr-2">{i.label}</span><span>{i.count}</span></div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-1"><div className="h-full" style={{ width: `${(i.count / max) * 100}%`, background: color }}/></div>
          </div>
        ))}
        {(!items || items.length === 0) && <div className="text-white/55 text-sm">No data yet.</div>}
      </div>
    </div>
  );
}

// ---------- Sponsors (invite brand accounts) ----------
function SponsorsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", company: "", logo_url: "", website: "" });
  const [createdInfo, setCreatedInfo] = useState(null);

  const load = () => api.get("/admin/sponsors").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email || !form.name || !form.company) {
      toast.error("Email, name and company are required");
      return;
    }
    try {
      const { data } = await api.post("/admin/sponsors", form);
      if (data.created) {
        setCreatedInfo({ email: data.email, password: data.temp_password });
        toast.success("Sponsor account created!");
      } else if (data.promoted) {
        toast.success("Existing user promoted to sponsor");
      }
      setForm({ email: "", name: "", company: "", logo_url: "", website: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="glass-card p-6 lg:col-span-2">
        <h3 className="font-display font-bold text-lg mb-3">Active sponsor accounts</h3>
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.user_id} className="flex items-center justify-between p-3 rounded-xl bg-white/5" data-testid={`sponsor-row-${r.user_id}`}>
              <div className="flex items-center gap-3">
                {r.sponsor_logo
                  ? <img src={r.sponsor_logo} alt={r.company} className="w-10 h-10 rounded-lg bg-white p-1 object-contain"/>
                  : <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neonyellow to-neonpink flex items-center justify-center font-bold text-ink">{(r.company || r.name)[0]?.toUpperCase()}</div>}
                <div>
                  <div className="font-bold">{r.company || r.name}</div>
                  <div className="text-xs text-white/55">{r.email} · {r.name}</div>
                </div>
              </div>
              {r.sponsor_website && (
                <a href={r.sponsor_website} target="_blank" rel="noopener noreferrer" className="text-xs text-neoncyan hover:underline">{new URL(r.sponsor_website).hostname}</a>
              )}
            </div>
          ))}
          {rows.length === 0 && <div className="text-white/55 text-sm">No sponsors yet. Invite one →</div>}
        </div>
      </div>
      <div className="glass-card p-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><UserPlus size={18}/> Invite sponsor</h3>
        <div className="space-y-3 mt-3">
          <Input label="Sponsor email" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="sponsor-form-email"/>
          <Input label="Contact name" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="sponsor-form-name"/>
          <Input label="Company" value={form.company} onChange={v => setForm({ ...form, company: v })} testid="sponsor-form-company"/>
          <Input label="Logo URL" value={form.logo_url} onChange={v => setForm({ ...form, logo_url: v })} testid="sponsor-form-logo"/>
          <Input label="Website" value={form.website} onChange={v => setForm({ ...form, website: v })} testid="sponsor-form-website"/>
          <button onClick={invite} className="btn-primary-neon text-sm w-full" data-testid="sponsor-form-submit">Create sponsor account</button>
          {createdInfo && (
            <div className="mt-3 p-3 rounded-xl bg-neongreen/10 border border-neongreen/30 text-sm" data-testid="sponsor-created-info">
              <div className="font-bold text-neongreen mb-1">Account created</div>
              <div className="text-white/80">Email: <code className="text-neoncyan">{createdInfo.email}</code></div>
              <div className="text-white/80">Temp password: <code className="text-neoncyan">{createdInfo.password}</code></div>
              <div className="text-white/55 text-xs mt-1">Share these credentials with the sponsor (manually for now).</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Form helpers ----------
function Input({ label, value, onChange, type = "text", testid, full, disabled }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} disabled={disabled}
        className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-neonpink disabled:opacity-50"/>
    </label>
  );
}
function Textarea({ label, value, onChange, testid, full, rows = 3 }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
      <textarea rows={rows} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-neonpink"/>
    </label>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
      <select value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-neonpink">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
